import React, {ChangeEvent, useEffect, useState} from 'react';
import {useWatch} from "rc-field-form";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {addSignUpData} from "../redux/formSlice";
import {Form} from "antd";

const UseCreatePersonalForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<any>();
  const values = useWatch([], form);
  const dispatch = useDispatch();

  const DEFAULT_STRENGTH_MSG = t('Password must be at least 6 characters');

  class DefaultValidPassword {
    weak = false;
    okay = false;
    good = false;
    strong = false;
  }

  useEffect(() => {
    const phone = form.getFieldValue('phone') || '';
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      setIsDisabledPhoneBtn(false);
    }
  }, []);

  class DefaultPasswordRequirments {
    length = {
      state: false,
      message: t('Password must be at least 6 characters')
    };
  }

  const [passwordRequirments, setPasswordRequirments] = useState(
    new DefaultPasswordRequirments()
  );

  const [validPassword, setValidPassword] = useState(
    new DefaultValidPassword()
  );

  const [strengthMessage, setStrengthMessage] =
    useState<string>(DEFAULT_STRENGTH_MSG);

  const [isDisabledBtn, setIsDisabledBtn] = useState<boolean>(true);
  const [isDisabledPhoneBtn, setIsDisabledPhoneBtn] = useState<boolean>(true);

  const passwordValidator = (value: string) => {
    if (!value) {
      setStrengthMessage(DEFAULT_STRENGTH_MSG);
      setPasswordRequirments(new DefaultPasswordRequirments());
      setValidPassword(new DefaultValidPassword());

      return Promise.reject(new Error(t('Password must be at least 6 characters')));
    }

    let newStrengthMessage = '';
    const newValidPassword = new DefaultValidPassword();

    const lengthReq = value.length >= 6; //ensure password >= 8 symbols

    const newPasswordReq = { ...passwordRequirments };
    newPasswordReq.length.state = !!lengthReq;

    for (let [key, value] of Object.entries(newPasswordReq)) {
      if (!value.state) {
        newStrengthMessage = value.message;
        break;
      }
    }

    setPasswordRequirments({ ...newPasswordReq });

    if (
      !lengthReq
    ) {
      setStrengthMessage(newStrengthMessage);
      setValidPassword({ ...newValidPassword });
      return Promise.reject('');
    }

    if (value.length >= 6) {
      newValidPassword.weak = true;
      newStrengthMessage = t('weak');
    }
    if (value.length >= 12) {
      newValidPassword.okay = true;
      newStrengthMessage = t('okay');
    }
    if (value.length >= 14) {
      newValidPassword.good = true;
      newStrengthMessage = t('good');
    }
    if (value.length >= 16) {
      newValidPassword.strong = true;
      newStrengthMessage = t('strong');
    }

    setStrengthMessage(newStrengthMessage);
    setValidPassword({ ...newValidPassword });
    return Promise.resolve();
  };

  const handleBtnDisable = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsDisabledBtn(false))
      .catch(() => setIsDisabledBtn(true));
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPosition = input.selectionStart || 0;
    const previousValue = input.value;

    let inputValue = previousValue.replace(/\D/g, '');

    let formattedValue = "";
    if (inputValue.length > 0) formattedValue = `(${inputValue.slice(0, 3)}`;
    if (inputValue.length > 3) formattedValue += `) ${inputValue.slice(3, 6)}`;
    if (inputValue.length > 6) formattedValue += `-${inputValue.slice(6, 10)}`;

    let newCursorPosition = cursorPosition;

    const charsBeforeCursor = previousValue.slice(0, cursorPosition);
    const digitsBeforeCursor = charsBeforeCursor.replace(/\D/g, '').length;

    let digitCount = 0;
    newCursorPosition = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        newCursorPosition = i + 1;
        break;
      }
    }

    form.setFieldValue('phone', formattedValue);

    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    if (inputValue.length === 10) {
      setIsDisabledPhoneBtn(false);
    } else {
      setIsDisabledPhoneBtn(true);
    }
  };

  const onFinish = async () => {
    const formData = { ...values };

    dispatch(addSignUpData(formData));
  };

  useEffect(() => {
    handleBtnDisable();
  }, [form, values]);


  return {
    form,
    values,
    onFinish,
    passwordRequirments,
    passwordValidator,
    validPassword,
    strengthMessage,
    isDisabledBtn,
    isDisabledPhoneBtn,
    setIsDisabledBtn,
    handlePhoneChange
  };
};

export default UseCreatePersonalForm;